import React, { useState } from "react";
import {
  Modal,
  Box,
  Button,
  Typography,
  Grid,
  Card,
  Stack,
  Divider,
  Alert,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import TagIcon from "@mui/icons-material/Tag";

const SectionLabel = ({ children }) => (
  <Typography
    variant="overline"
    sx={{
      fontSize: "0.68rem",
      letterSpacing: "0.08em",
      color: "text.disabled",
      display: "block",
      mb: 1,
    }}
  >
    {children}
  </Typography>
);

const NameChip = ({ label, value }) => (
  <Box
    sx={{
      flex: 1,
      bgcolor: "action.hover",
      borderRadius: 2,
      px: 1.5,
      py: 1.25,
    }}
  >
    <Typography
      variant="caption"
      color="text.disabled"
      display="block"
      mb={0.25}
    >
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500}>
      {value}
    </Typography>
  </Box>
);

const NotarizationRequestConfirmationModal = React.memo(
  ({
    open,
    setOpen,
    onConfirm,
    loading,
    contractorSigning,
    signingOrder,
    ownerSigning,
    values,
  }) => {
    const [isAcknowledged, setIsAcknowledged] = useState(false);

    return (
      <Modal open={open}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            maxWidth: "1100px",
            width: "85vw",
            height: "90vh",
            maxHeight: "750px",
            overflow: "hidden",
            bgcolor: "background.paper",
            borderRadius: "10px",
            boxShadow: 24,
            p: { xs: 1, sm: 1.5 },
          }}
        >
          <Typography
            variant="h6"
            align="center"
            color="primary"
            fontWeight="bold"
            mb={0}
          >
            Signer Information
          </Typography>

          <Typography
            align="center"
            color="text.secondary"
            variant="body2"
            mb={2}
          >
            {!contractorSigning?.notarizationNeeded &&
            !contractorSigning?.signingNeeded
              ? "Overview of the signer"
              : "Overview of the signers"}
          </Typography>

          <Box sx={{ height: "calc(100% - 120px)", overflowY: "auto" }}>
            <Grid container sx={{ gap: 3, justifyContent: "center" }}>
              {contractorSigning?.notarizationNeeded ||
              contractorSigning?.signingNeeded ? (
                <Grid size={{ xs: 12, sm: 12, md: 6 }} height={"100%"}>
                  <Card
                    sx={{
                      p: { xs: 2, sm: 3 },
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: "20px",
                      border: "1px solid rgba(25, 118, 210, 0.1)",
                      boxShadow:
                        "0 8px 24px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(25, 118, 210, 0.05) inset",
                      position: "relative",
                      overflow: "hidden",
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        boxShadow:
                          "0 12px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(25, 118, 210, 0.1) inset",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 3,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          flex: 1,
                        }}
                      >
                        <Typography
                          fontWeight="bold"
                          variant="h5"
                          sx={{
                            color: "primary.main",
                            position: "relative",
                            zIndex: 1,
                          }}
                        >
                          Contractor
                        </Typography>
                      </Box>
                    </Box>

                    <Stack
                      spacing={2}
                      sx={{ position: "relative", zIndex: 1, flex: 1 }}
                    >
                      <SectionLabel>Name</SectionLabel>
                      <Stack
                        direction="row"
                        spacing={1}
                        mb={2.5}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        <NameChip label="First" value={values?.cFirst} />
                        <NameChip label="Middle" value={values?.cMiddle} />
                        <NameChip label="Last" value={values?.cLast} />
                      </Stack>

                      <Divider sx={{ mb: 2 }} />

                      {contractorSigning?.notarizationNeeded && (
                        <>
                          <SectionLabel>Address</SectionLabel>
                          <Box mb={2.5}>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: { xs: "column", sm: "row" },
                                alignItems: { xs: "flex-start", sm: "center" },
                                gap: { xs: 1.5, sm: 4 },
                                py: 1.25,
                                borderBottom: "1px solid",
                                borderColor: "divider",
                              }}
                            >
                              {/* Street address */}
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1.5,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: 1.5,
                                    bgcolor: "action.hover",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    color: "text.secondary",
                                  }}
                                >
                                  <LocationOnOutlinedIcon
                                    sx={{ fontSize: 17 }}
                                  />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    variant="caption"
                                    color="text.disabled"
                                    display="block"
                                    lineHeight={1.3}
                                  >
                                    Street address
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    fontWeight={500}
                                    noWrap
                                  >
                                    {values?.cAddress}
                                  </Typography>
                                </Box>
                              </Box>

                              {/* City */}
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1.5,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: 1.5,
                                    bgcolor: "action.hover",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    color: "text.secondary",
                                  }}
                                >
                                  <ApartmentOutlinedIcon
                                    sx={{ fontSize: 17 }}
                                  />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    variant="caption"
                                    color="text.disabled"
                                    display="block"
                                    lineHeight={1.3}
                                  >
                                    City
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    fontWeight={500}
                                    noWrap
                                  >
                                    {values?.cCity}
                                  </Typography>
                                </Box>
                              </Box>

                              {/* ZIP */}
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1.5,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: 1.5,
                                    bgcolor: "action.hover",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    color: "text.secondary",
                                  }}
                                >
                                  <TagIcon sx={{ fontSize: 17 }} />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    variant="caption"
                                    color="text.disabled"
                                    display="block"
                                    lineHeight={1.3}
                                  >
                                    ZIP code
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    fontWeight={500}
                                    noWrap
                                  >
                                    {values?.cZip}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        </>
                      )}

                      <SectionLabel>Contact</SectionLabel>
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            alignItems: { xs: "flex-start", sm: "center" },
                            gap: { xs: 1.5, sm: 4 },
                            py: 1.25,
                            borderBottom: "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          {/* Email */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: 34,
                                height: 34,
                                borderRadius: 1.5,
                                bgcolor: "action.hover",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                color: "text.secondary",
                              }}
                            >
                              <EmailOutlinedIcon sx={{ fontSize: 17 }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="caption"
                                color="text.disabled"
                                display="block"
                                lineHeight={1.3}
                              >
                                Email
                              </Typography>
                              <Typography
                                variant="body2"
                                fontWeight={500}
                                noWrap
                              >
                                {values?.cEmail}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Mobile */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: 34,
                                height: 34,
                                borderRadius: 1.5,
                                bgcolor: "action.hover",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                color: "text.secondary",
                              }}
                            >
                              <PhoneOutlinedIcon sx={{ fontSize: 17 }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="caption"
                                color="text.disabled"
                                display="block"
                                lineHeight={1.3}
                              >
                                Mobile
                              </Typography>
                              <Typography
                                variant="body2"
                                fontWeight={500}
                                noWrap
                              >
                                {values?.cMobile}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Stack>
                  </Card>
                </Grid>
              ) : null}

              <Grid
                size={{
                  xs: 12,
                  sm: 12,
                  md:
                    contractorSigning?.notarizationNeeded ||
                    contractorSigning?.signingNeeded
                      ? 6
                      : 12,
                }}
                height={"100%"}
              >
                {(ownerSigning?.notarizationNeeded ||
                  ownerSigning?.signingNeeded) && (
                  <Card
                    sx={{
                      p: { xs: 2, sm: 3 },
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: "20px",
                      border: "1px solid rgba(25, 118, 210, 0.1)",
                      boxShadow:
                        "0 8px 24px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(25, 118, 210, 0.05) inset",
                      position: "relative",
                      overflow: "hidden",
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        boxShadow:
                          "0 12px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(25, 118, 210, 0.1) inset",
                      },
                    }}
                  >
                    <Typography
                      fontWeight="bold"
                      align="center"
                      mb={3}
                      variant="h5"
                      sx={{
                        color: "primary.main",
                        position: "relative",
                        zIndex: 1,
                      }}
                    >
                      Owner/Agent
                    </Typography>

                    <Stack spacing={2}>
                      <SectionLabel>Name</SectionLabel>
                      <Stack
                        direction="row"
                        spacing={1}
                        mb={2.5}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        <NameChip label="First" value={values?.oFirst} />
                        <NameChip label="Middle" value={values?.oMiddle} />
                        <NameChip label="Last" value={values?.oLast} />
                      </Stack>

                      <Divider sx={{ mb: 2 }} />

                      {ownerSigning?.notarizationNeeded && (
                        <>
                          <SectionLabel>Address</SectionLabel>
                          <Box mb={2.5}>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: { xs: "column", sm: "row" },
                                alignItems: { xs: "flex-start", sm: "center" },
                                gap: { xs: 1.5, sm: 4 },
                                py: 1.25,
                                borderBottom: "1px solid",
                                borderColor: "divider",
                              }}
                            >
                              {/* Street address */}
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1.5,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: 1.5,
                                    bgcolor: "action.hover",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    color: "text.secondary",
                                  }}
                                >
                                  <LocationOnOutlinedIcon
                                    sx={{ fontSize: 17 }}
                                  />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    variant="caption"
                                    color="text.disabled"
                                    display="block"
                                    lineHeight={1.3}
                                  >
                                    Street address
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    fontWeight={500}
                                    noWrap
                                  >
                                    {values?.oAddress}
                                  </Typography>
                                </Box>
                              </Box>

                              {/* City */}
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1.5,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: 1.5,
                                    bgcolor: "action.hover",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    color: "text.secondary",
                                  }}
                                >
                                  <ApartmentOutlinedIcon
                                    sx={{ fontSize: 17 }}
                                  />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    variant="caption"
                                    color="text.disabled"
                                    display="block"
                                    lineHeight={1.3}
                                  >
                                    City
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    fontWeight={500}
                                    noWrap
                                  >
                                    {values?.oCity}
                                  </Typography>
                                </Box>
                              </Box>

                              {/* ZIP */}
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1.5,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: 1.5,
                                    bgcolor: "action.hover",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    color: "text.secondary",
                                  }}
                                >
                                  <TagIcon sx={{ fontSize: 17 }} />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    variant="caption"
                                    color="text.disabled"
                                    display="block"
                                    lineHeight={1.3}
                                  >
                                    ZIP code
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    fontWeight={500}
                                    noWrap
                                  >
                                    {values?.oZip}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        </>
                      )}

                      <SectionLabel>Contact</SectionLabel>
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            alignItems: { xs: "flex-start", sm: "center" },
                            gap: { xs: 1.5, sm: 4 },
                            py: 1.25,
                            borderBottom: "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          {/* Email */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: 34,
                                height: 34,
                                borderRadius: 1.5,
                                bgcolor: "action.hover",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                color: "text.secondary",
                              }}
                            >
                              <EmailOutlinedIcon sx={{ fontSize: 17 }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="caption"
                                color="text.disabled"
                                display="block"
                                lineHeight={1.3}
                              >
                                Email
                              </Typography>
                              <Typography
                                variant="body2"
                                fontWeight={500}
                                noWrap
                              >
                                {values?.oEmail}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Mobile */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: 34,
                                height: 34,
                                borderRadius: 1.5,
                                bgcolor: "action.hover",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                color: "text.secondary",
                              }}
                            >
                              <PhoneOutlinedIcon sx={{ fontSize: 17 }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="caption"
                                color="text.disabled"
                                display="block"
                                lineHeight={1.3}
                              >
                                Mobile
                              </Typography>
                              <Typography
                                variant="body2"
                                fontWeight={500}
                                noWrap
                              >
                                {values?.oMobile}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Stack>
                  </Card>
                )}
              </Grid>
            </Grid>
            <Alert severity="warning" sx={{ mt: 1.5, borderRadius: "8px" }}>
              <Typography variant="body2">
                Details cannot be changed after submission. Please review
                everything carefully before confirming. This action is
                permanent.
              </Typography>
            </Alert>

            <Box sx={{ px: 2, py: 1 }}>
              <FormControlLabel
                disabled={loading}
                control={
                  <Checkbox
                    color="primary"
                    checked={isAcknowledged}
                    onChange={(event) =>
                      setIsAcknowledged(event.target.checked)
                    }
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: "text.primary" }}>
                    I confirm that all the details above are correct and
                    understand that they cannot be edited after submission.
                  </Typography>
                }
              />
            </Box>
          </Box>

          <Box
            sx={{ mt: 1.5, display: "flex", justifyContent: "space-between" }}
          >
            <Button
              disabled={loading}
              sx={{ mr: 1 }}
              fullWidth
              color="error"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <LoadingButton
              loading={loading}
              disabled={!isAcknowledged}
              sx={{ ml: 1 }}
              fullWidth
              variant="contained"
              color="success"
              onClick={onConfirm}
            >
              Submit
            </LoadingButton>
          </Box>
        </Box>
      </Modal>
    );
  },
);

export default NotarizationRequestConfirmationModal;
